import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useMemo, useState } from 'react';
import { useTranslation } from '@ibk/shared-translation';
import { VoucherType } from '@ibk/shared-types';
import { Icon } from '@ibk/shared-ui';
import { formatCurrency } from '@ibk/shared-utils';
import { BeneficiaryStatus, BeneficiaryStatusInProduct, } from '../../../../types';
import { BeneficiaryDetailsTooltip } from '../../../BeneficiaryDetailsTooltip';
import styles from './BeneficiariesList.module.css';
const safeTrim = (str) => {
    return str?.trim() ?? '';
};
export function BeneficiariesList({ rechargeData }) {
    const { t } = useTranslation();
    const [searchTerm, setSearchTerm] = useState('');
    const allBeneficiaries = useMemo(() => {
        if (!rechargeData.vouchers)
            return [];
        const voucherTypeOrder = [VoucherType.VR_VA, VoucherType.VA, VoucherType.VR];
        const getVoucherTypePriority = (voucherType) => {
            const index = voucherTypeOrder.findIndex(type => voucherType.toLowerCase().includes(type.toLowerCase()));
            return index === -1 ? voucherTypeOrder.length : index;
        };
        const beneficiariesByProduct = rechargeData.vouchers.map(voucher => ({
            voucherType: voucher.voucherTypeDescription,
            voucherTypePriority: getVoucherTypePriority(voucher.voucherTypeDescription),
            beneficiaries: voucher.beneficiaries || [],
        }));
        beneficiariesByProduct.sort((a, b) => a.voucherTypePriority - b.voucherTypePriority);
        const sortedBeneficiaries = beneficiariesByProduct.flatMap(product => {
            const sortedProductBeneficiaries = [...product.beneficiaries].sort((a, b) => {
                const nameA = `${safeTrim(a.firstName)} ${safeTrim(a.lastName)}`.toLowerCase();
                const nameB = `${safeTrim(b.firstName)} ${safeTrim(b.lastName)}`.toLowerCase();
                return nameA.localeCompare(nameB, 'pt-BR');
            });
            return sortedProductBeneficiaries.map(beneficiary => ({
                ...beneficiary,
                voucherType: product.voucherType,
                voucherTypePriority: product.voucherTypePriority,
            }));
        });
        return sortedBeneficiaries;
    }, [rechargeData.vouchers]);
    const filteredBeneficiaries = useMemo(() => {
        if (!searchTerm.trim())
            return allBeneficiaries;
        const term = searchTerm.toLowerCase();
        return allBeneficiaries.filter(beneficiary => `${beneficiary.firstName} ${beneficiary.lastName}`.toLowerCase().includes(term));
    }, [allBeneficiaries, searchTerm]);
    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
    };
    const convertToVoucherBeneficiary = (beneficiary) => {
        const mapStatus = (status) => {
            return status === 'WORKING' ? BeneficiaryStatus.WORKING : BeneficiaryStatus.INACTIVE;
        };
        return {
            id: beneficiary.id,
            document: beneficiary.document,
            firstName: beneficiary.firstName,
            lastName: beneficiary.lastName,
            employeeCode: beneficiary.employeeCode,
            deliveryLocationId: beneficiary.deliveryLocationId,
            birthDate: new Date(beneficiary.birthDate),
            phoneNumber: beneficiary.phoneNumber,
            email: beneficiary.email,
            status: mapStatus(beneficiary.status),
            employeeId: beneficiary.employeeId,
            jobRole: beneficiary.jobRole,
            isNew: false,
            productValue: beneficiary.value,
            statusInProduct: BeneficiaryStatusInProduct.ACTIVE,
            productDaysQuantity: 0,
            productDiscountValue: beneficiary.discount,
            contractType: beneficiary.contractType,
            costCenter: beneficiary.costCenter,
            department: beneficiary.department,
            category: beneficiary.category,
            employmentBond: beneficiary.employmentBond,
            branch: beneficiary.branch,
            union: beneficiary.union,
            startDate: beneficiary.startDate,
            endDate: beneficiary.endDate,
            hireDate: beneficiary.hireDate,
        };
    };
    if (!rechargeData.vouchers || rechargeData.vouchers.length === 0) {
        return null;
    }
    return (_jsxs("section", { className: styles.container, "data-testid": 'containerBeneficiariesList', children: [_jsx("h3", { className: styles.sectionTitle, "data-testid": 'textViewBeneficiariesListTitle', children: t('benefits.recharge-details.beneficiaries') }), _jsx("div", { className: styles.searchContainer, children: _jsxs("div", { className: styles.searchInputWrapper, children: [_jsx(Icon, { name: 'search', className: styles.searchIcon }), _jsx("input", { type: 'text', placeholder: t('benefits.recharge-details.search-beneficiaries'), value: searchTerm, onChange: handleSearchChange, className: styles.searchInput, "data-testid": 'inputTextFieldBeneficiariesListSearch' })] }) }), _jsx("div", { className: styles.beneficiariesList, "data-testid": 'scrollAreaBeneficiariesList', children: filteredBeneficiaries.length > 0 ? (filteredBeneficiaries.map((beneficiary, index) => (_jsxs("div", { className: styles.beneficiaryItem, "data-testid": `containerBeneficiariesListItem${index}`, children: [_jsxs("div", { className: styles.beneficiaryInfo, children: [_jsxs("div", { className: styles.beneficiaryName, "data-testid": `textViewBeneficiariesListItemName${index}`, children: [beneficiary.firstName, " ", beneficiary.lastName, _jsx("span", { className: styles.detailsTooltip, "data-testid": `viewBeneficiariesListItemDetailsTooltip${index}`, children: _jsx(BeneficiaryDetailsTooltip, { beneficiary: convertToVoucherBeneficiary(beneficiary), testIdIdentifier: `BeneficiariesListItemDetailsTooltip${index}` }) })] }), _jsxs("div", { className: styles.beneficiaryIdentifiers, children: [beneficiary.contractType && (_jsx("span", { className: styles.beneficiaryJobRole, "data-testid": `textViewBeneficiariesListItemJobRole${index}`, children: beneficiary.contractType })), beneficiary.employeeCode && (_jsx("span", { className: styles.beneficiaryEmployeeCode, "data-testid": `textViewBeneficiariesListItemEmployeeCode${index}`, children: beneficiary.employeeCode }))] })] }), _jsxs("div", { className: styles.beneficiaryValue, "data-testid": `textViewBeneficiariesListItemValue${index}`, children: [formatCurrency(beneficiary.value), t('benefits.recharge-details.monthly-value')] })] }, `${beneficiary.document}-${index}`)))) : (_jsx("div", { className: styles.noResults, "data-testid": 'textViewBeneficiariesListNoResults', children: t('benefits.recharge-details.no-beneficiaries') })) })] }));
}
